import React, { useState, ChangeEvent, FormEvent } from 'react';
import { Container, Button, Typography, Box, CircularProgress, Accordion, AccordionSummary, AccordionDetails, Divider, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import workAnalyzerService from '../services/workAnalyzerService';
import ImprovementEvaluationSummaryDisplay from '../components/ImprovementPlanComponents/ImprovementEvaluationSummaryDisplay';
import { PropertyAnalysis, ImprovementPlanItem } from '../models/improvementPlan';

const formatCurrency = (value: number): string => `$${value.toLocaleString()}`;

const ImprovementEvaluation: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [propertyAnalysis, setPropertyAnalysis] = useState<PropertyAnalysis | null>(null);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFiles([...files, ...Array.from(event.target.files)]);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (files.length === 0) return;

    setLoading(true);
    setLoadingMessage('Processing files...');

    try {
      const uploadedIds: string[] = [];

      for (const file of files) {
        setLoadingMessage(`Uploading ${file.name}...`);
        const uploadResult = await workAnalyzerService.upload(file);

        if (!uploadResult || !uploadResult.id) {
          console.error(`Failed to upload file: ${file.name}`);
          continue;
        }

        uploadedIds.push(uploadResult.id);
      }

      if (uploadedIds.length === 0) {
        setLoadingMessage('No files could be uploaded. Please try again.');
        setLoading(false);
        return;
      }

      setLoadingMessage('Analyzing the property...');
      const analysisResult: PropertyAnalysis = await workAnalyzerService.processBatchImprovements(uploadedIds);

      setPropertyAnalysis(analysisResult);
      setLoadingMessage('Analysis completed successfully.');
    } catch (error) {
      console.error('Error during processing:', error);
      setLoadingMessage('An error occurred. Please try again.');
    } finally {
      setLoading(false);
      setFiles([]);
    }
  };

  const handleNewAnalysis = () => {
    setPropertyAnalysis(null);
    setFiles([]);
    setLoadingMessage('');
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pb: 4 }}>
      <Typography component="h1" variant="h5" gutterBottom>
        Property Improvement Analysis
      </Typography>

      {!propertyAnalysis && (
        <Box component="form" onSubmit={handleSubmit} encType="multipart/form-data" sx={{ mt: 4 }}>
          <Button variant="contained" component="label" fullWidth sx={{ mb: 2 }}>
            Select files to upload
            <input type="file" multiple hidden onChange={handleFileChange} />
          </Button>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body1" sx={{ ml: 2 }}>
                {loadingMessage}
              </Typography>
            </Box>
          ) : (
            <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 4 }}>
              Upload Files
            </Button>
          )}
        </Box>
      )}

      {propertyAnalysis && (
        <Box sx={{ mt: 4 }}>
          <ImprovementEvaluationSummaryDisplay
            totalLowCost={propertyAnalysis.ImprovementPlan.reduce(
              (sum, item) =>
                sum +
                item.CostBreakdown.reduce(
                  (breakdownSum, breakdownItem) =>
                    breakdownSum + breakdownItem.MaterialCostLow + breakdownItem.LaborCostLow,
                  0
                ),
              0
            )}
            totalHighCost={propertyAnalysis.ImprovementPlan.reduce(
              (sum, item) =>
                sum +
                item.CostBreakdown.reduce(
                  (breakdownSum, breakdownItem) =>
                    breakdownSum + breakdownItem.MaterialCostHigh + breakdownItem.LaborCostHigh,
                  0
                ),
              0
            )}
            renovationValue={propertyAnalysis.ImprovementPlan.reduce(
              (sum, item) => sum + item.ValueIncreaseHigh,
              0
            )}
            score={propertyAnalysis.InvestmentScore}
            address="1234 Placeholder St"
            currentPrice={200000}
            scoreDetails={propertyAnalysis.InvestmentScoreDetail}
            valueAddRange={{
              minValue: propertyAnalysis.ImprovementPlan.reduce(
                (sum, item) => sum + item.ValueIncreaseLow,
                0
              ),
              maxValue: propertyAnalysis.ImprovementPlan.reduce(
                (sum, item) => sum + item.ValueIncreaseHigh,
                0
              ),
            }}
          />

          <Typography component="h2" variant="h6" sx={{ mt: 4, mb: 2 }}>
            Improvement Plan
          </Typography>
          {propertyAnalysis.ImprovementPlan.map((plan: ImprovementPlanItem, index: number) => (
            <Accordion key={index} sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#f7f7f7' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {plan.Item} - {formatCurrency(plan.ValueIncreaseLow)} - {formatCurrency(plan.ValueIncreaseHigh)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Description:</strong> {plan.Description}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Cost Breakdown:</strong>
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell><strong>Labor</strong></TableCell>
                      <TableCell><strong>Material</strong></TableCell>
                      <TableCell><strong>Total</strong></TableCell>
                      <TableCell sx={{ color: 'green', fontWeight: 'bold' }}><strong>Value</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {['High', 'Medium', 'Low'].map((level, rowIndex) => (
                      <TableRow key={rowIndex}>
                        <TableCell>{level}</TableCell>
                        <TableCell>
                          {formatCurrency(
                            plan.CostBreakdown.reduce(
                              (sum, breakdown) =>
                                sum +
                                (level === 'High' ? breakdown.LaborCostHigh : level === 'Medium' ? breakdown.LaborCostMedium : breakdown.LaborCostLow),
                              0
                            )
                          )}
                        </TableCell>
                        <TableCell>
                          {formatCurrency(
                            plan.CostBreakdown.reduce(
                              (sum, breakdown) =>
                                sum +
                                (level === 'High'
                                  ? breakdown.MaterialCostHigh
                                  : level === 'Medium'
                                  ? breakdown.MaterialCostMedium
                                  : breakdown.MaterialCostLow),
                              0
                            )
                          )}
                        </TableCell>
                        <TableCell>
                          {formatCurrency(
                            plan.CostBreakdown.reduce(
                              (sum, breakdown) =>
                                sum +
                                (level === 'High'
                                  ? breakdown.MaterialCostHigh + breakdown.LaborCostHigh
                                  : level === 'Medium'
                                  ? breakdown.MaterialCostMedium + breakdown.LaborCostMedium
                                  : breakdown.MaterialCostLow + breakdown.LaborCostLow),
                              0
                            )
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'green', fontWeight: 'bold' }}>
                          {formatCurrency(
                            plan.ValueIncreaseHigh && level === 'High'
                              ? plan.ValueIncreaseHigh
                              : plan.ValueIncreaseMedium && level === 'Medium'
                              ? plan.ValueIncreaseMedium
                              : plan.ValueIncreaseLow
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2">
                  <strong>Impacted Rooms:</strong> {plan.ImpactedRooms.join(', ')}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}

          <Button variant="outlined" color="secondary" fullWidth sx={{ mt: 4 }} onClick={handleNewAnalysis}>
            Submit Another Analysis
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default ImprovementEvaluation;
